import React from 'react';
import { useStore } from '@/store/index';
import { Insight } from '@/types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  getFilteredRowModel,
  RowSelectionState,
} from "@tanstack/react-table";
import { DataTablePagination } from '@/components/ui/data-table-pagination';
import { SummaryPopover } from '@/components/shared/SummaryPopover';

interface InsightsTableProps {
  onInsightSelect: (insight: Insight) => void;
}

export function InsightsTable({ onInsightSelect }: InsightsTableProps) {
  const { insights } = useStore();
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});

  // Add function to check if insight is in "cooking" state
  const isCooking = (insight: Insight): boolean => {
    return insight.created_at === '0' || insight.created_at === '';
  };

  // Define columns for the insights table
  const columns: ColumnDef<Insight>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <input
          type="checkbox"
          checked={table.getIsAllRowsSelected()}
          onChange={(e) => table.toggleAllRowsSelected(!!e.target.checked)}
          className="h-4 w-4"
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          onChange={(e) => row.toggleSelected(!!e.target.checked)}
          className="h-4 w-4"
        />
      ),
    },
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }) => {
        const insight = row.original;
        const cooking = isCooking(insight);
        
        return (
          <SummaryPopover summary={insight.summary}>
            <div 
              className={`${cooking ? 'text-gray-400 italic' : 'cursor-pointer hover:text-blue-600'}`}
              onClick={(e) => {
                if (!cooking) {
                  e.stopPropagation();
                  onInsightSelect(insight);
                }
              }}
            >
              {cooking ? `${insight.title} (Generating...)` : insight.title || 'Untitled Insight'}
            </div>
          </SummaryPopover>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      cell: ({ row }) => {
        const timestamp = row.original.created_at;
        const date = new Date(timestamp);
        const cooking = timestamp === '0' || timestamp === '' || date.getTime() === 0;

        return cooking ? (
          <span className="text-gray-400 italic">Generating...</span>
        ) : (
          date.toLocaleDateString()
        );
      },
    },
  ];

  // Memoize the filtered data
  const filteredInsights = React.useMemo(() => {
    return insights.filter(insight => !insight.is_questions);
  }, [insights]);

  // Add sorting function to put cooking insights at the top
  const sortedInsights = React.useMemo(() => {
    return [...filteredInsights].sort((a, b) => {
      const aDate = new Date(a.created_at).getTime();
      const bDate = new Date(b.created_at).getTime();
      
      // If one is cooking (timestamp = 0), put it first
      if ((a.created_at === '0' || a.created_at === '') && b.created_at !== '0' && b.created_at !== '') return -1;
      if ((b.created_at === '0' || b.created_at === '') && a.created_at !== '0' && a.created_at !== '') return 1;
      
      // Otherwise sort by created_at descending
      return bDate - aDate;
    });
  }, [filteredInsights]);

  // Initialize the table with sorted data
  const table = useReactTable({
    data: sortedInsights,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },
  });

  // Modify handleRowClick to prevent clicking on cooking insights
  const handleRowClick = (insight: Insight) => {
    if (!isCooking(insight)) {
      onInsightSelect(insight);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="rounded-md border flex-1 overflow-hidden flex flex-col">
        <div className="overflow-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="h-8">
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className="h-8">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className="h-8 cursor-pointer hover:bg-muted/50"
                    onClick={() => handleRowClick(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="py-1">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-16 text-center">
                    No insights available.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="mt-auto border-t">
          <DataTablePagination table={table} />
        </div>
      </div>
    </div>
  );
} 